import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApiClient from '../../api/adminApiClient';
import config from '../../utils/config';
import { FiArrowLeft, FiEdit2, FiSave, FiX } from 'react-icons/fi';

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    clientName: '',
    active: true
  });

  // Load project data
  const fetchProjectData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get project details
      const endpoint = config.ADMIN_ENDPOINTS.PROJECT_DETAILS?.(projectId) || `/admin/projects/${projectId}`;
      const response = await adminApiClient.get(endpoint);
      
      if (response.status === 'success') {
        const projectData = response.data;
        setProject(projectData);
        setFormData({
          name: projectData.name || '',
          description: projectData.description || '',
          clientName: projectData.clientName || '',
          active: projectData.active !== undefined ? projectData.active : true
        });
      } else {
        setError('Failed to fetch project details');
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
      setError('An error occurred while fetching project details');
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Save project changes
  const handleSaveProject = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Update project endpoint
      const endpoint = config.ADMIN_ENDPOINTS.UPDATE_PROJECT?.(projectId) || `/admin/projects/${projectId}`;
      const response = await adminApiClient.patch(endpoint, formData);
      
      if (response.status === 'success') {
        setProject(response.data);
        setIsEditMode(false);
        // Show success message
        alert('Project updated successfully');
      } else {
        setError('Failed to update project');
      }
    } catch (error) {
      console.error('Error updating project:', error);
      setError('An error occurred while updating project details');
    } finally {
      setIsLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <AdminLayout title="Project Details">
        <div className="container px-6 py-8 mx-auto max-w-7xl">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Project Details">
        <div className="container px-6 py-8 mx-auto max-w-7xl">
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
            <div className="flex items-center">
              <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {error}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Project Details">
      <div className="container px-6 py-8 mx-auto max-w-7xl">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 btn btn-secondary text-smd"
          >
            <FiArrowLeft className="mr-2" />
            Back to Projects
          </button>
        </div>

        {/* Project Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-2">
              <div className="flex items-center">
                <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Total Sessions</h3>
                  <p className="text-3xl font-semibold text-blue-600">{project.stats?.totalSessions || 0}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-2">
              <div className="flex items-center">
                <div className="p-2 rounded-full bg-green-100 text-green-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Total Messages</h3>
                  <p className="text-3xl font-semibold text-green-600">{project.stats?.totalMessages || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Project Information */}
        <div className="bg-white shadow rounded-lg overflow-hidden mb-6 p-2">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold">Project Information</h2>
            <button
              onClick={() => setIsEditMode(!isEditMode)}
              className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium btn text-smd ${
                isEditMode
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 btn-secondary'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 btn-primary'
              }`}
            >
              {isEditMode ? (
                <>
                  <FiX className="mr-2" />
                  Cancel Editing
                </>
              ) : (
                <>
                  <FiEdit2 className="mr-2" />
                  Edit Project
                </>
              )}
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-m font-medium text-gray-500 mb-1">Project ID</h3>
                <p className="font-mono bg-gray-50 p-2 rounded text-smd">{projectId}</p>
              </div>

              <div>
                <h3 className="text-m font-medium text-gray-500 mb-1">Project Name</h3>
                {isEditMode ? (
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-smd pl-2 pr-2"
                  />
                ) : (
                  <p className="text-gray-900">{project.name}</p>
                )}
              </div>

              <div>
                <h3 className="text-m font-medium text-gray-500 mb-1">Client Name</h3>
                {isEditMode ? (
                  <input
                    type="text"
                    name="clientName"
                    value={formData.clientName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 pl-2 pr-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-smd"
                  />
                ) : (
                  <p className="text-gray-900 text-smd">{project.clientName || 'Not specified'}</p>
                )}
              </div>

              <div>
                <h3 className="text-m font-medium text-gray-500 mb-1">Status</h3>
                {isEditMode ? (
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="active"
                      checked={formData.active}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-gray-900">Active</span>
                  </label>
                ) : (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    project.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {project.active ? 'Active' : 'Inactive'}
                  </span>
                )}
              </div>

              <div className="md:col-span-2">
                <h3 className="text-m font-medium text-gray-500 mb-1">Description</h3>
                {isEditMode ? (
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 p-2 text-smd border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 whitespace-pre-line">{project.description || 'No description provided'}</p>
                )}
              </div>

              <div>
                <h3 className="text-m font-medium text-gray-500 mb-1">Created At</h3>
                <p className="text-gray-900">{formatDate(project.createdAt)}</p>
              </div>

              <div>
                <h3 className="text-m font-medium text-gray-500 mb-1">Last Updated</h3>
                <p className="text-gray-900">{formatDate(project.updatedAt)}</p>
              </div>
            </div>

            {isEditMode && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveProject}
                  disabled={isLoading}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-smd btn btn-primary"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* LLM Configuration */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">LLM Configuration</h2>
          </div>

          <div className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Primary LLM (Decision Making)</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="text-m font-medium text-gray-500">Model</h4>
                      <p className="mt-1 text-smd text-gray-900">{project.primaryLLM?.model || 'Not set'}</p>
                    </div>
                    <div>
                      <h4 className="text-m font-medium text-gray-500">Temperature</h4>
                      <p className="mt-1 text-smd text-gray-900">{project.primaryLLM?.temperature || 'Not set'}</p>
                    </div>
                    <div>
                      <h4 className="text-m font-medium text-gray-500">Max Tokens</h4>
                      <p className="mt-1 text-smd text-gray-900">{project.primaryLLM?.maxTokens || 'Not set'}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-m font-medium text-gray-500 mb-2">System Prompt</h4>
                    <div className="bg-white rounded border border-gray-200 p-3">
                      <p className="text-smd p-2 text-gray-900 whitespace-pre-wrap">{project.primaryLLM?.systemPrompt || 'No system prompt set'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Secondary LLM (Response Generation)</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="text-m font-medium text-gray-500">Model</h4>
                      <p className="mt-1 text-smd text-gray-900">{project.secondaryLLM?.model || 'Not set'}</p>
                    </div>
                    <div>
                      <h4 className="text-m font-medium text-gray-500">Temperature</h4>
                      <p className="mt-1 text-smd text-gray-900">{project.secondaryLLM?.temperature || 'Not set'}</p>
                    </div>
                    <div>
                      <h4 className="text-m font-medium text-gray-500">Max Tokens</h4>
                      <p className="mt-1 text-smd text-gray-900">{project.secondaryLLM?.maxTokens || 'Not set'}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-m font-medium text-gray-500 mb-2">System Prompt</h4>
                    <div className="bg-white rounded border border-gray-200 p-3">
                      <p className="text-smd p-2 text-gray-900 whitespace-pre-wrap">{project.secondaryLLM?.systemPrompt || 'No system prompt set'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ProjectDetails; 